import { Reveal, MaskLine } from "@/components/site/Reveal";
import CTABand from "@/components/site/CTABand";
import { TIMELINE, CHAPTERS, RAW_MATERIALS } from "@/data/content";

const CHAPTER_IMGS = ["images/heritage_1961.jpeg", "images/workers_packaging.jpeg", "images/mixer_dough.jpeg", "images/ship_export.jpeg"];

const AboutPage = () => (
  <main data-testid="page-about">
    {/* HERO */}
    <section className="relative overflow-hidden bg-brand-choc pt-36 pb-24 md:pt-48 md:pb-32 text-brand-cream">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <MaskLine><p className="eyebrow-gold">About us · Est. 1961</p></MaskLine>
        <h1 className="mt-6 font-display font-light tracking-tight leading-[0.98] text-5xl sm:text-6xl lg:text-8xl">
          <MaskLine delay={0.1}>Three generations.</MaskLine>
          <MaskLine delay={0.3}><span className="italic text-brand-gold">One oven's promise.</span></MaskLine>
        </h1>
        <MaskLine delay={0.55}>
          <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-brand-cream/80">
            National Biscuit &amp; Sweet Manufacturers is a third-generation family bakery from Ulhasnagar,
            Maharashtra — among the first packaged-food units in the region, and one of its most renowned
            names ever since.
          </p>
        </MaskLine>
      </div>
    </section>

    {/* FOUNDERS */}
    <section className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 py-24 md:py-32">
      <div className="grid items-center gap-12 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <img src="images/heritage_1961.jpeg" alt="Seth Papumal and Seth Narimal Nagpal in the first bakery, 1961" className="aspect-[4/5] w-full rounded-2xl object-cover" />
        </Reveal>
        <div className="lg:col-span-7">
          <Reveal><p className="eyebrow">The founders</p></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 font-display font-light text-4xl md:text-5xl tracking-tight">
              Seth Papumal (Parsram) &amp; Seth Narimal Nagpal
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-7 text-base md:text-lg leading-relaxed text-brand-muted">
              In a single small room, with a single hand oven, they baked their first biscuits in 1961 —
              and set a standard the family still keeps. No shortcuts, no compromises: the finest
              carefully selected raw materials, honest weight and a wrapper that always held what it promised.
            </p>
            <p className="mt-4 text-base md:text-lg leading-relaxed text-brand-muted">
              That hand oven grew into one of the area's first packaged-food units, then into markets
              across all of India, and — since 1998 — into export markets across Africa and the Gulf.
              The goodwill they earned is the business's founding capital; it has never been spent, only reinvested.
            </p>
          </Reveal>
        </div>
      </div>
    </section>

    {/* MANIFESTO CHAPTERS */}
    <section className="border-y border-brand-line bg-brand-sand/60">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 py-24 md:py-32">
        <Reveal><p className="eyebrow">The National manifesto</p></Reveal>
        <div className="mt-14 space-y-20 md:space-y-28" data-testid="manifesto">
          {CHAPTERS.map((c, i) => (
            <div key={c.no} className="grid gap-8 lg:grid-cols-12 lg:items-center">
              <div className={`lg:col-span-6 ${i % 2 ? "lg:order-1 lg:text-right" : ""}`}>
                <Reveal delay={0.08}>
                  <h3 className="font-display font-light text-3xl md:text-5xl tracking-tight">{c.title}</h3>
                  <p className="mt-5 text-base leading-relaxed text-brand-muted">{c.body}</p>
                </Reveal>
              </div>
              <Reveal delay={0.15} className={`lg:col-span-6 ${i % 2 ? "lg:order-2" : ""}`}>
                <img src={CHAPTER_IMGS[i]} alt={c.title} className="aspect-[16/10] w-full rounded-2xl object-cover" />
              </Reveal>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* TIMELINE */}
    <section className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 py-24 md:py-32">
      <Reveal><p className="eyebrow">Sixty years, five chapters</p></Reveal>
      <div className="mt-12" data-testid="timeline">
        {TIMELINE.map((t, i) => (
          <Reveal key={t.year} delay={i * 0.05}>
            <div className="grid gap-4 border-t border-brand-line py-8 md:grid-cols-12 md:items-baseline">
              <p className="font-mono text-sm uppercase tracking-[0.2em] text-brand-red md:col-span-3">{t.year}</p>
              <h3 className="font-display text-2xl md:text-3xl font-light md:col-span-4">{t.title}</h3>
              <p className="text-sm md:text-base leading-relaxed text-brand-muted md:col-span-5">{t.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    {/* RAW MATERIALS */}
    <section className="noise-dark bg-brand-choc text-brand-cream">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 py-24 md:py-32">
        <Reveal>
          <p className="eyebrow-gold">Nothing but the finest</p>
          <h2 className="mt-5 font-display font-light text-4xl md:text-6xl tracking-tight">
            Carefully selected. <span className="italic text-brand-gold">Every single time.</span>
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="raw-materials">
          {RAW_MATERIALS.map((r, i) => (
            <Reveal key={r.name} delay={i * 0.08}>
              <div className="h-full rounded-2xl border border-brand-cream/15 bg-brand-cream/5 p-7">
                <h3 className="mt-3 font-display text-2xl">{r.name}</h3>
                <p className="mt-3 text-sm leading-relaxed text-brand-cream/70">{r.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <CTABand />
  </main>
);

export default AboutPage;
